// remote-config.service.ts
import { Injectable } from '@angular/core';
import { AngularFireRemoteConfig } from '@angular/fire/compat/remote-config';
import { environment } from 'src/environments/environment';

const featureFlags = {
  rules: 'rules',
  integrationCredentials: 'integration_credentials',
  situations: 'situations',
  premisesAutocomplete: 'premises_autocomplete',
  exporterInterval: 'exporter_interval',
  verification_checklist: 'verification_checklist',
  hide_end_date_service_ai: 'hide_end_date_service_ai',
  future_date_alert: 'future_date_alert',
  employeeMessageLimit: 'employee_message_limit',
};

export type FeatureFlags = Record<keyof typeof featureFlags, FlagValue>;

/**
 * The allow-list rule, over a value rather than a key: `true` releases to
 * everyone, a missing or non-string value to nobody, and a string is a
 * comma-separated list of company ids. Prefer `isEnabledForCompany` -- this is
 * exported for the callers that hold the value instead of the key.
 */
export const isFlagValueEnabledForCompany = (
  value: FlagValue,
  companyId?: string | null
): boolean => {
  if (value === true) {
    return true;
  }

  if (!companyId || typeof value !== 'string') {
    return false;
  }

  return value
    .split(',')
    .map((id) => id.trim())
    .includes(companyId);
};

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  featureFlags: FeatureFlags = {} as FeatureFlags;

  constructor(private remoteConfig: AngularFireRemoteConfig) {
    Object.keys(featureFlags).forEach(
      (key) => (this.featureFlags[key as keyof FeatureFlags] = false)
    );
  }

  async load(): Promise<void> {
    await this.remoteConfig.fetchAndActivate();

    const promises: Promise<[string, FlagValue]>[] = [];
    Object.keys(featureFlags).forEach((key) =>
      promises.push(this.getValue(key))
    );

    const result = await Promise.all(promises);
    result.forEach(
      ([key, value]) => (this.featureFlags[key as keyof FeatureFlags] = value)
    );
  }

  async getValue(key: string): Promise<[string, FlagValue]> {
    const parts = environment.firebase.authDomain.split('.');
    if (parts.length > 0) {
      const valueAsString = (
        await this.remoteConfig.getValue(
          `${featureFlags[key as keyof typeof featureFlags]}_${parts[0]}`
        )
      ).asString();

      return [key, this.convertFromStringValue(valueAsString)];
    }
    return [key, null];
  }

  convertFromStringValue(str: string) {
    if (str === 'true') return true;
    if (str === 'false') return false;

    return str;
  }

  /** The documented way to read a company-scoped flag (CLAUDE.md rule 24). */
  isEnabledForCompany(
    key: keyof FeatureFlags,
    companyId: string | null | undefined
  ): boolean {
    return isFlagValueEnabledForCompany(this.featureFlags[key], companyId);
  }
}

export type FlagValue = boolean | string | null;
